import { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";

import { formatPrice } from "../../utils/format";
import AddressAutocomplete from "../AddressAutocomplete/AddressAutocomplete";
import AutoGrowTextarea from "../AutoGrowTextarea/AutoGrowTextarea";

/**
 * The comparable-sales curation control inside the report editor.
 *
 * The comp set is addressed BY ADDRESS — the one first-class, agent-meaningful
 * identifier each comparable carries end-to-end. The agent removes comps they
 * don't like and adds replacements via the Zillow typeahead (so addresses are
 * real); a free-typed address is allowed too and is handed to the AI's
 * grounded search to verify. Applying the change re-runs ONLY the comparable
 * analysis (one AI pass, ~a minute) — every other section keeps its edits.
 */

const MAX_ADDRESSES = 10;

const Wrap = styled.div`
  margin-top: 16px;
  padding-top: 14px;
  border-top: 1px dashed #e5e7eb;
`;

const Label = styled.div`
  font-size: 0.78rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #0e7e7a;
  margin-bottom: 6px;
`;

const Hint = styled.p`
  font-size: 0.85rem;
  color: #6b7280;
  line-height: 1.5;
  margin: 0 0 10px;
`;

const CompRow = styled.div`
  border: 1px solid ${(p) => (p.$editing ? "#bfe0dc" : "#e5e7eb")};
  border-radius: 10px;
  margin-bottom: 8px;
  background: #ffffff;
`;

const AddressRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  padding: 9px 12px;
`;

const AddressText = styled.div`
  flex: 1;
  min-width: 0;
  font-size: 0.92rem;
  color: #1a1a1a;
  font-weight: 600;

  @media (max-width: 560px) {
    flex: 1 0 100%;
  }
`;

const AddressMeta = styled.span`
  display: block;
  font-size: 0.78rem;
  font-weight: 400;
  color: #6b7280;
  margin-top: 1px;
`;

const NewTag = styled.span`
  font-size: 0.66rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 3px 8px;
  border-radius: 999px;
  background: #e6f4f3;
  color: #0b605d;
  white-space: nowrap;
`;

const RemoveAddress = styled.button`
  font-size: 0.82rem;
  font-weight: 600;
  color: #b91c1c;
  background: none;
  border: 1px solid #fecaca;
  border-radius: 8px;
  padding: 6px 10px;
  min-height: 36px;
  cursor: pointer;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: #fef2f2;
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const AddWrap = styled.div`
  margin: 10px 0 4px;
`;

const ApplyRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 12px;
`;

const ApplyButton = styled.button`
  font-size: 0.88rem;
  font-weight: 700;
  color: #ffffff;
  background: #0e7e7a;
  border: 1px solid #0e7e7a;
  border-radius: 999px;
  padding: 0 20px;
  min-height: 44px;
  cursor: pointer;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: #0b6360;
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

// Pricing v2: credit-aware agents hesitate on every AI button unless the free
// ones say so (CREDITS_UI_MAP rule 5).
const FreeNote = styled.span`
  font-size: 0.8rem;
  font-weight: 600;
  color: #6b7280;
  white-space: nowrap;
`;

const CancelButton = styled.button`
  font-size: 0.86rem;
  font-weight: 600;
  color: #374151;
  background: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 999px;
  padding: 0 16px;
  min-height: 44px;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #f3f4f6;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PendingNote = styled.p`
  font-size: 0.85rem;
  color: #4a4a4a;
  margin: 8px 0 0;
  line-height: 1.5;
`;

const WarningNote = styled.p`
  font-size: 0.85rem;
  color: #92400e;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 8px;
  padding: 10px 12px;
  margin: 10px 0 0;
  line-height: 1.5;
`;

/** @param {string | number | null | undefined} n */

/**
 * Recompute a comp's derived numbers after a hand edit: price per square foot
 * from its own price/sqft, and the vs-subject deltas from the report's
 * property details — the same math the worker runs at generation time, so
 * hand-edited comps stay consistent with generated ones.
 */
/**
 * @param {Record<string, any>} comp
 * @param {Record<string, any> | null | undefined} subject
 */
export const withDerivedCompFields = (comp, subject) => {
  /** @param {unknown} v */
  const num = (v) => {
    const n = Number(v);
    return Number.isFinite(n) && n !== 0 ? n : null;
  };
  const next = { ...comp };
  const price = num(next.price);
  const sqft = num(next.square_feet);
  next.price_per_sqft = price && sqft ? price / sqft : null;
  const subjSqft = num(subject?.square_feet);
  const subjPrice = num(subject?.listing_price);
  const subjPpsf = subjPrice && subjSqft ? subjPrice / subjSqft : null;
  next.size_difference = sqft && subjSqft ? Math.round(sqft - subjSqft) : null;
  next.price_difference = price && subjPrice ? price - subjPrice : null;
  next.price_per_sqft_difference =
    next.price_per_sqft && subjPpsf ? next.price_per_sqft - subjPpsf : null;
  return next;
};

const FieldsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(110px, 1fr));
  gap: 8px 10px;
  margin: 10px 0;
`;

const SmallField = styled.label`
  display: block;
  margin: 0;
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6b7280;

  input,
  select {
    display: block;
    width: 100%;
    margin-top: 3px;
    font-size: 0.9rem;
    font-weight: 500;
    color: #1a1a1a;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    padding: 8px 10px;
    min-height: 40px;
    background: #ffffff;
    font-family: inherit;

    &:focus {
      outline: none;
      border-color: #0e7e7a;
      box-shadow: 0 0 0 3px rgba(14, 126, 122, 0.15);
    }
    &:disabled {
      opacity: 0.5;
    }
  }
`;

const CompFieldsWrap = styled.div`
  border-top: 1px dashed #e5e7eb;
  margin: 0 12px;
  padding: 4px 0 12px;
`;

const SummaryLabel = styled.div`
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6b7280;
  margin: 8px 0 3px;
`;

const SummaryTextarea = styled.textarea`
  width: 100%;
  min-height: 64px;
  font-size: 0.9rem;
  line-height: 1.5;
  color: #1a1a1a;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 9px 11px;
  font-family: inherit;
  resize: none;
  overflow: hidden;

  &:focus {
    outline: none;
    border-color: #0e7e7a;
    box-shadow: 0 0 0 3px rgba(14, 126, 122, 0.15);
  }
  &:disabled {
    opacity: 0.5;
  }
`;

const EditCompButton = styled.button`
  font-size: 0.82rem;
  font-weight: 600;
  color: ${(p) => (p.$on ? "#0b605d" : "#374151")};
  background: ${(p) => (p.$on ? "#e6f4f3" : "#ffffff")};
  border: 1px solid ${(p) => (p.$on ? "#bfe0dc" : "#d1d5db")};
  border-radius: 8px;
  padding: 6px 10px;
  min-height: 36px;
  cursor: pointer;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: ${(p) => (p.$on ? "#dff2ef" : "#f3f4f6")};
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

// The comp's editable facts: [field, label, input attrs].
/** @type {Array<[string, string, import("react").InputHTMLAttributes<HTMLInputElement>]>} */
const COMP_FACT_FIELDS = [
  ["price", "Price ($)", { type: "number", min: 0 }],
  ["bedrooms", "Beds", { type: "number", min: 0, step: 1 }],
  ["bathrooms", "Baths", { type: "number", min: 0, step: 0.5 }],
  ["square_feet", "Sq ft", { type: "number", min: 0, step: 1 }],
  ["year_built", "Built", { type: "number", min: 1700, step: 1 }],
  ["days_on_market", "Days on market", { type: "number", min: 0, step: 1 }],
];

/** The current comp addresses as stored on the section. */
/**
 * @param {Record<string, any> | null | undefined} section
 * @returns {string[]}
 */
export const compAddresses = (section) =>
  /** @type {Record<string, any>[]} */ (section?.comparable_properties || [])
    .map((c) => String(c?.address || "").trim())
    .filter(Boolean);

/**
 * Requested addresses that did NOT land in the rebuilt comp set — the AI's
 * grounded search couldn't verify them (typo'd address, or a home with no
 * listing record). Matching is on the normalized street part, since the AI
 * may reformat city/state/zip.
 * @param {string[]} requested
 * @param {string[]} returned
 */
export const unverifiedCompAddresses = (requested, returned) => {
  /** @param {string} a */
  const normalize = (a) => (a || "").toLowerCase().replace(/[^a-z0-9]/g, "");
  return (requested || []).filter((req) => {
    const street = normalize((req || "").split(",")[0]);
    return !street || !(returned || []).some((addr) => normalize(addr).includes(street));
  });
};

/** Auto-grows to fit its content so the whole summary is always readable. */
/** @param {{value: string} & import("react").TextareaHTMLAttributes<HTMLTextAreaElement>} props */

/**
 * @param {{
 *   section: Record<string, any>,
 *   subject?: Record<string, any>,
 *   disabled: boolean,
 *   isUpdating: boolean,
 *   warning?: string | null,
 *   onUpdate: (addresses: string[], excluded: string[]) => void,
 *   onReplaceSection?: (section: Record<string, any>) => void,
 * }} props
 */
const CompsEditor = ({
  section,
  subject,
  disabled,
  isUpdating,
  warning,
  onUpdate,
  onReplaceSection,
}) => {
  const stored = useMemo(() => compAddresses(section), [section]);
  // metaByAddress lets rows show price/status for comps that already exist.
  const metaByAddress = useMemo(() => {
    const map = /** @type {Record<string, Record<string, any>>} */ ({});
    for (const c of /** @type {Record<string, any>[]} */ (section?.comparable_properties || [])) {
      if (c && c.address != null) map[String(c.address).trim()] = c;
    }
    return map;
  }, [section]);

  const nextWorkingIdRef = useRef(0);
  /** @param {string} address */
  const makeWorkingRow = (address) => ({
    id: `comp-row-${++nextWorkingIdRef.current}`,
    address,
  });
  const [workingRows, setWorkingRows] = useState(() => stored.map(makeWorkingRow));
  const working = workingRows.map((row) => row.address);
  // An address edit changes storedKey when the parent accepts it. The local
  // working row has already been updated in the same event, so that particular
  // resync must not replace the row object (which would drop input focus) or
  // discard unrelated staged additions/removals.
  const pendingAddressEditRef = useRef(/** @type {{ from: string, to: string } | null} */ (null));
  const storedKey = stored.join("|");
  // Resync the working list only when the underlying ADDRESS LIST changes (an
  // AI update landed, or the doc reloaded). Keyed on the joined string, NOT
  // the memoized array: `stored` gets a new identity every time the section
  // object is replaced (any hand edit to a comp's price/summary), and syncing
  // on identity silently wiped the agent's staged address curation.
  // biome-ignore lint/correctness/useExhaustiveDependencies: storedKey is the change signal for `stored`
  useEffect(() => {
    const addressEdit = pendingAddressEditRef.current;
    const parentAcceptedAddressEdit =
      addressEdit &&
      /** @type {Record<string, any>[]} */ (section?.comparable_properties || []).some(
        (comp) => String(comp?.address ?? "").trim() === addressEdit.to
      );
    if (parentAcceptedAddressEdit) {
      pendingAddressEditRef.current = null;
      return;
    }
    setWorkingRows((previous) => {
      const unused = [...previous];
      return stored.map((address) => {
        const matchIndex = unused.findIndex((row) => row.address === address);
        if (matchIndex < 0) return makeWorkingRow(address);
        return unused.splice(matchIndex, 1)[0];
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storedKey]);

  const dirty = working.join("|") !== storedKey;
  const excluded = stored.filter((a) => !working.includes(a));

  /** @param {string} address */
  const addAddress = (address) => {
    const addr = (address || "").trim();
    if (!addr) return;
    setWorkingRows((prev) => {
      if (prev.some((row) => row.address.toLowerCase() === addr.toLowerCase())) return prev;
      if (prev.length >= MAX_ADDRESSES) return prev;
      return [...prev, makeWorkingRow(addr)];
    });
  };

  /** @param {string} addr */
  const removeAddress = (addr) =>
    setWorkingRows((prev) => prev.filter((row) => row.address !== addr));

  // Details panels start OPEN so everything is readable without extra taps;
  // agents can tuck individual comps away. Tracks the collapsed set.
  const [collapsedAddrs, setCollapsedAddrs] = useState(/** @type {string[]} */ ([]));
  /** @param {string} addr */
  const toggleCollapsed = (addr) =>
    setCollapsedAddrs((prev) =>
      prev.includes(addr) ? prev.filter((a) => a !== addr) : [...prev, addr]
    );

  // Hand-edit one field of one comp: update it in place, recompute the derived
  // numbers ($/SF + vs-subject deltas), and hand the whole section back up —
  // it flows through the normal auto-save like any other edit.
  /**
   * @param {string} addr
   * @param {string} field
   * @param {string} rawValue
   */
  const setCompField = (addr, field, rawValue) => {
    if (!onReplaceSection) return;
    const comps = /** @type {Record<string, any>[]} */ (section?.comparable_properties || []);
    const nextComps = comps.map((c) => {
      if ((c?.address || "").trim() !== addr) return c;
      let value = /** @type {string | number | null} */ (rawValue);
      if (field !== "address" && field !== "comparison_summary" && field !== "status") {
        value = rawValue === "" ? null : Number(rawValue);
        if (Number.isNaN(value)) value = null;
      }
      return withDerivedCompFields({ ...c, [field]: value }, subject);
    });
    onReplaceSection({ ...section, comparable_properties: nextComps });
    if (field === "address") {
      // Keep the row identity in step with the stored section in the SAME
      // commit — the resync effect lags one render, and that mismatch frame
      // unmounted the details panel (dropping input focus every keystroke).
      const nextAddr = (rawValue || "").trim();
      pendingAddressEditRef.current = { from: addr, to: nextAddr };
      setWorkingRows((prev) =>
        prev.map((row) => (row.address === addr ? { ...row, address: nextAddr } : row))
      );
      setCollapsedAddrs((prev) => prev.map((a) => (a === addr ? nextAddr : a)));
    }
  };

  const controlsDisabled = disabled || isUpdating;

  return (
    <Wrap>
      <Label>Comparable homes</Label>
      <Hint>
        Review the comparable properties below. Remove any that aren't a good fit, or add additional
        homes you'd like to include in your pricing analysis. When you apply changes, Listino
        re-runs the comparable analysis with your picks (about a minute).
      </Hint>

      {workingRows.map(({ id: rowId, address: addr }) => {
        const pendingAddressEdit = pendingAddressEditRef.current;
        const meta =
          metaByAddress[addr] ||
          (pendingAddressEdit?.to === addr ? metaByAddress[pendingAddressEdit.from] : undefined);
        const metaBits = meta
          ? [formatPrice(meta.price), meta.status ? String(meta.status) : ""].filter(Boolean)
          : [];
        const isEditing = !!meta && !!onReplaceSection && !collapsedAddrs.includes(addr);
        return (
          <CompRow key={rowId} $editing={isEditing}>
            <AddressRow>
              <AddressText>
                {addr}
                {meta ? (
                  metaBits.length > 0 && <AddressMeta>{metaBits.join(" · ")}</AddressMeta>
                ) : (
                  <AddressMeta>Added — AI will research this home when you apply</AddressMeta>
                )}
              </AddressText>
              {!meta && <NewTag>New</NewTag>}
              {meta && onReplaceSection && (
                <EditCompButton
                  type="button"
                  $on={isEditing}
                  disabled={disabled}
                  aria-label={`${isEditing ? "Hide" : "Show"} details for ${addr}`}
                  onClick={() => toggleCollapsed(addr)}
                >
                  {isEditing ? "Hide details" : "Details"}
                </EditCompButton>
              )}
              <RemoveAddress
                type="button"
                disabled={controlsDisabled || working.length <= 1}
                aria-label={`Remove ${addr}`}
                onClick={() => removeAddress(addr)}
              >
                Remove
              </RemoveAddress>
            </AddressRow>
            {isEditing && (
              <CompFieldsWrap>
                <FieldsGrid>
                  <SmallField style={{ gridColumn: "1 / -1" }}>
                    Address
                    <input
                      type="text"
                      value={meta.address || ""}
                      disabled={disabled}
                      aria-label="Comp address"
                      onChange={(e) => setCompField(addr, "address", e.target.value)}
                    />
                  </SmallField>
                  {COMP_FACT_FIELDS.map(([field, label, attrs]) => (
                    <SmallField key={field}>
                      {label}
                      <input
                        {...attrs}
                        value={meta[field] ?? ""}
                        disabled={disabled}
                        aria-label={`Comp ${label}`}
                        onChange={(e) => setCompField(addr, field, e.target.value)}
                      />
                    </SmallField>
                  ))}
                  <SmallField>
                    Status
                    <select
                      value={(meta.status || "active").toLowerCase()}
                      disabled={disabled}
                      aria-label="Comp status"
                      onChange={(e) => setCompField(addr, "status", e.target.value)}
                    >
                      {/* Legacy docs store "Sold"/"Active" — normalize so the
                          select shows the real stored status, not the first option. */}
                      <option value="active">Active</option>
                      <option value="pending">Pending</option>
                      <option value="sold">Sold</option>
                    </select>
                  </SmallField>
                </FieldsGrid>
                <SummaryLabel>Comparison summary (shown in the document)</SummaryLabel>
                <AutoGrowTextarea
                  textareaComponent={SummaryTextarea}
                  value={meta.comparison_summary || ""}
                  disabled={disabled}
                  aria-label="Comp comparison summary"
                  onChange={(e) => setCompField(addr, "comparison_summary", e.target.value)}
                />
              </CompFieldsWrap>
            )}
          </CompRow>
        );
      })}

      {working.length < MAX_ADDRESSES && (
        <AddWrap>
          <AddressAutocomplete
            id={`add-comp-${section.section_id}`}
            placeholder="Add a comparable — start typing an address…"
            disabled={controlsDisabled}
            clearAfterSelect
            onSelect={(s) => addAddress(s.display)}
            onFreeText={addAddress}
          />
        </AddWrap>
      )}

      {(dirty || isUpdating) && (
        <ApplyRow>
          <ApplyButton
            type="button"
            disabled={controlsDisabled || working.length === 0}
            onClick={() => onUpdate(working, excluded)}
          >
            {isUpdating ? "Updating comparables…" : "Update comparables with AI"}
          </ApplyButton>
          <FreeNote>Always free</FreeNote>
          {dirty && !isUpdating && (
            <CancelButton
              type="button"
              onClick={() => {
                pendingAddressEditRef.current = null;
                setWorkingRows((previous) => {
                  const unused = [...previous];
                  return stored.map((address) => {
                    const matchIndex = unused.findIndex((row) => row.address === address);
                    return matchIndex < 0
                      ? makeWorkingRow(address)
                      : unused.splice(matchIndex, 1)[0];
                  });
                });
              }}
            >
              Cancel
            </CancelButton>
          )}
        </ApplyRow>
      )}
      {dirty && !isUpdating && (
        <PendingNote>
          {excluded.length > 0 && (
            <>
              Removing: <strong>{excluded.join("; ")}</strong>.{" "}
            </>
          )}
          The pricing, verdict, and analysis in this part will be rebuilt from your comp list — and
          if the pricing moves, mentions of it in your summary and conclusion are updated to match.
        </PendingNote>
      )}
      {isUpdating && (
        <PendingNote>
          AI is researching your comparables and rebuilding the pricing analysis — hang tight, this
          usually takes about a minute.
        </PendingNote>
      )}
      {warning && !isUpdating && <WarningNote>{warning}</WarningNote>}
    </Wrap>
  );
};

export default CompsEditor;
